import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import { Heading } from '../../util/typography/index.js';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import {
    PageHeader,
    PageSection,
    PageSubSection,
    RelatedContent,
    CrossLink,
    IndicationPageIntro,
    AnchorLinks,
    PrimaryClaim,
    KMCurve,
    ComparisonTable,
    SecondaryCallout,
    EndpointDisplay,
    NCCNcallout,
    ReferencesBlock,
    TextBlock,
    FeaturedStat,
    PatientProfile,
} from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import HNSCC_Squamous from '../../components/StudyDesign/__study-design/hnscc-squamous.js';
import { jobCodes } from '../../util/data/jobCodes';
import styled from 'styled-components';
import { IndicationText } from '../../components/IndicationPageIntro/index.js';
import ComponentWrapper from '../../components/layout/ComponentWrapper.js';
import NotationBlock from '../../util/typography/NotationBlock.js';
// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-689 - Clinical Trial Results | HCP`,
    keywords: `keynote 689, keynote-689`,
    description: `Health care professionals may find clinical trial results from KEYNOTE-689 for a treatment option given as neoadjuvant treatment then continued as part of an adjuvant treatment regimen in certain adult patients with resectable locally advanced head and neck squamous cell carcinoma (HNSCC) (CPS≥1).`,
    schemaJsonLD: [
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/hnscc-squamous-cell-carcinoma/","@type":"ApprovedIndication","name":"KEYTRUDA® and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated for the treatment of adult patients with resectable locally advanced head and neck squamous cell carcinoma (HNSCC) whose tumors express PD-L1 [Combined Positive Score (CPS) ≥1] as determined by an FDA-authorized test, as a single agent as neoadjuvant treatment, continued as adjuvant treatment in combination with radiotherapy (RT) with or without cisplatin and then as a single agent."}`,
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/hnscc-squamous-cell-carcinoma/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/hnscc-squamous-cell-carcinoma/","@type":"MedicalWebPage","name":"Neoadjuvant Monotherapy and Then Continued in Combination With Adjuvant Radiotherapy ± Cisplatin Followed by Monotherapy for Resectable Locally Advanced HNSCC (CPS ≥1)","description":"KEYTRUDA is the FIRST and ONLY Anti–PD-1 Therapy Approved as Part of a Neoadjuvant Followed by Adjuvant Treatment Regimen to Demonstrate a Statistically Significant Improvement in Event-Free Survival (EFS) vs Standard of Care (Adjuvant RT ± Cisplatin) in Resectable Locally Advanced Head and Neck Squamous Cell Carcinoma (CPS ≥1)"}`,
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/hnscc-squamous-cell-carcinoma/","@type":"MedicalTrial","name":"KEYNOTE⁠-⁠689","description":"KEYNOTE-689: A randomized, multicenter, open-label, active-controlled trial in 714 patients with resectable, locally advanced (stage III–IVA)d head and neck squamous cell carcinoma (LA HNSCC)","trialDesign":"RandomizedTrial"}`,
    `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/hnscc-squamous-cell-carcinoma/","@type":"MedicalGuideline","guideline":"Pembrolizumab (KEYTRUDA) is the FIRST and ONLY Anti–PD-1 Therapy Recommended in the NCCN Guidelines® as Part of a Neoadjuvant Followed by Adjuvant Treatment Regimen for Certain Head and Neck Cancers With PD-L1 CPS ≥1","recognizingAuthority":"Selected Recommendations from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Head and Neck Cancers"}`,
    ]
};

//from /src/util/data/indications.js
const indicationId = 38;
const jobCode = jobCodes[37].jobCode;

const primaryClaimData = `KEYTRUDA is the FIRST and ONLY Anti&ndash;PD-1 Therapy Approved as Part of a Neoadjuvant Followed by Adjuvant Treatment Regimen to Demonstrate a Statistically Significant Improvement in Event-Free Survival (EFS) vs Standard of Care (Adjuvant RT ± Cisplatin) in Resectable Locally Advanced Head and Neck Squamous Cell Carcinoma (CPS ≥1)`

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action',
        },
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support',
        },
    },
];

const crossLinkContent = {
    sections: [
        {
            heading: 'Additional Clinical Data for KEYTRUDA',
            items: [
                {
                    text: 'HNSCC 1L Combo Therapy',
                    url: '/efficacy/hnscc-first-line-combination-therapy/',
                },
                {
                    text: 'HNSCC 1L Mono Therapy',
                    url: '/efficacy/hnscc-first-line-monotherapy/'
                }
            ],
        },
        {
            heading: 'Related Information',
            items: [
                {
                    text: 'Selected Adverse Reactions',
                    url: '/safety/adverse-reactions/hnscc-squamous-cell-carcinoma/',
                },
                { text: 'Dosing', url: '/dosing/options/#hnscc' },
                { text: 'PD&#8288;-&#8288L1 Testing & Scoring', url: '/biomarker-testing/pd-l1' },
            ]
        },
    ],
    interestLinks: {
        heading: 'You Might Be Interested In',
        items: [
            {
                eyebrow: 'Resources',
                text: 'Mechanism of Action',
                url: '/resources/mechanism-of-action/',
            },
        ],
    },
};

const anchorLinkModelData = [
    { label: 'Clinical Findings From KEYNOTE&#8288;-&#8288;689 for KEYTRUDA', },
    { label: 'Selected Recommendations from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Head and Neck Cancers'},
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;689 for KEYTRUDA' },
];

const kmCurveChartFootnotes = [
    {
        label: 'a.',
        text: 'Based on Cox regression model with Efron’s method of tie handling with treatment as a covariate stratified by primary tumor site and tumor stage.',
    },
    {
        label: 'b.',
        text: 'One-sided <i>P</i> value based on log-rank test stratified by primary tumor site and tumor stage. Compared to a one-sided <i>P</i> value boundary of 0.0124.',
    },
];

const ComparisonTableFootnotes = [
    {
        label: 'c.',
        text: 'From product-limit (Kaplan-Meier) method for censored data.'
    }
]

const pageReferences = [
    {
        label: '1.',
        text: `Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Head and Neck Cancers V.2.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed May 13, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org.`
    },
    {
        label: '2.',
        text: 'Uppaluri R, Haddad RI, Tao Y, et al. Neoadjuvant and adjuvant pembrolizumab in locally advanced head and neck cancer. <i>N Engl J Med</i>. 2025;393(1):37-50. doi:10.1056/NEJMoa2415434',
    }
];

const NCCNcalloutData = {
    title: `Pembrolizumab (KEYTRUDA) is the FIRST and ONLY Anti–PD-1 Therapy Recommended in the NCCN Guidelines® as Part of a Neoadjuvant Followed by Adjuvant Treatment Regimen for Certain Head and Neck Cancers With PD-L1 CPS ≥1<sup>1</sup>`,
    bodyCopy: `
    <h5>NCCN Category 2A: For Certain Stage III-IVA Head and Neck Cancers</h5>
    <p><strong>Select National Comprehensive Cancer Network® (NCCN®) induction/sequential systemic therapy and neoadjuvant immunotherapy recommendations:</strong></p>
    <ul>
        <li>Other recommended regimens:
            <ul>
                <li><strong>Pembrolizumab (KEYTRUDA) for stage III-IVA cancer of the oral cavity (CPS ≥1)</strong> as neoadjuvant therapy followed by adjuvant pembrolizumab with RT (with cisplatin if extranodal extension and/or positive margin) followed by adjuvant pembrolizumab</li>
            </ul>
        </li>
    </ul>
    <ul>
        <li>Useful in certain circumstances:
            <ul>
                <li><strong>Pembrolizumab (KEYTRUDA) for stage III-IVA cancer of the p16-negative oropharynx, hypopharynx, and larynx (CPS ≥1)</strong> as neoadjuvant therapy followed by adjuvant pembrolizumab with RT (with cisplatin if extranodal extension and/or positive margin) followed by adjuvant pembrolizumab</li>
            </ul>
        </li>
     </ul>
     <p>Pembrolizumab and berahyaluronidase alfa-pmph subcutaneous injection may be substituted for IV pembrolizumab. Pembrolizumab and berahyaluronidase alfa-pmph has different dosing and administration instructions compared to IV pembrolizumab.<sup>1</sup></p>
     <h5>The NCCN Guidelines Also Recommend PD-L1 Testing (CPS) by Immunohistochemistry as Clinically Indicated at the Time of Workup for the Following Head and Neck Cancer Types:</h5>
     <ul>
        <li>Oral cavity</li>
        <li>Oropharyngeal</li>
        <li>Hypopharyngeal</li>
        <li>Laryngeal</li>
     </ul>
    `
}

const NCCNcalloutDefinitions =`
<p>Category 2A = Based upon lower-level evidence, there is uniform NCCN consensus (≥85% support of the Panel) that the intervention is appropriate.<sup>1</sup></p>
<p>NCCN makes no warranties of any kind whatsoever regarding their content, use or application and disclaims any responsibility for their application or use in any way.</p>
<p>IV = intravenous.</p>
`;

const SecondaryCalloutDefinitions =`
<p>*Adjuvant RT ± Cisplatin.</p>
<p>LA HNSCC = locally advanced head and neck squamous cell carcinoma.</p>
`;

const NCCNcalloutReferences=[
    {label: '1.', text: 'Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Head and Neck Cancers V.2.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed May 13, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org.'}
];

const IndicationPageIntroWrapper = styled.div`
${ComponentWrapper} {
   margin-top: 0;
  }
`;


const Page = ({ location }) => {
    return (
        <DefaultLayout
            indicationId={indicationId}
            jobCode={jobCode}
            pageMeta={pageMeta}
            location={location}
        >
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results for KEYTRUDA® (pembrolizumab)" isBranding />
                    <PageSection bgColor="white">
                        <IndicationPageIntroWrapper>
                            <IndicationPageIntro indicationId={indicationId}  />
                        </IndicationPageIntroWrapper>
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">{primaryClaimData}</PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title={anchorLinkModelData[0].label}>
                            <PageSubSection title="Efficacy Results for EFS (Major Efficacy Outcome Measure) From the First Prespecified Interim Analysis (Median Follow-Up Time: 38.3 Months)">
                                <SecondaryCallout
                                    title="Superior EFS With KEYTRUDA Administered in the Neoadjuvant Setting Then Continued as Part of an Adjuvant Treatment Regimen With SOC* Followed by Monotherapy vs SOC Alone in Adult Patients With Resectable LA HNSCC (CPS ≥1)"
                                    alt=""
                                    definitions={SecondaryCalloutDefinitions}
                                />
                                <KMCurve
                                    title="Kaplan-Meier Estimates of EFS in KEYNOTE-689 in Adult Patients With Tumor PD-L1 Expression (CPS ≥1)"
                                    hr="HR<sup>a</sup>=0.70 (95% CI, 0.55–0.89; <i>P</i><sup>b</sup>=0.00140)"
                                    // limitation="Statistically significant improvement in EFS with KEYTRUDA + RT (with or without cisplatin) at the first-prespecified interim analysis in the overall population<sup>a</sup>"
                                    additionalInformation={[
                                        {
                                            text: `
                                        <ul>
                                            <li>EFS was defined as the time from randomization to the first occurrence of any of the following events: progression of disease that precluded definitive surgery, local or distant disease progression or recurrence, or death due to any cause</li>
                                        </ul>`},
                                    ]}
                                    footnotes={kmCurveChartFootnotes}
                                    definitions={['PD-L1 = programmed death ligand 1; PD&#8288;-&#8288;1 = programmed death receptor-1; HR = hazard ratio; CI = confidence interval; SOC = standard of care.']}
                                    image={() => (
                                        <StaticImage
                                            src="../../assets/KN-689-KM-curve.jpg"
                                            placeholder="blurred"
                                            alt="Kaplan-Meier Estimates of Event-Free Survival (EFS) With KEYTRUDA® (pembrolizumab) With Tumor PD-L1 CPS ≥1"
                                        />
                                    )}
                                />
                            </PageSubSection>

                            <ComparisonTable
                                title="Event-Free Survival (CPS ≥1)"
                                definitions=""
                                footnotes={[]}
                                data={[
                                    [
                                        {
                                            legendTitle: 'KEYTRUDA + SOC (n=347)',
                                        },
                                        {
                                            legendTitle: 'SOC (n=335)',
                                        },
                                    ],
                                    [
                                        {
                                            hrCiP: 'HR<sup>a</sup>=0.70 (95% CI, 0.55–0.89, <i>P</i><sup>b</sup>=0.00140)',
                                            colspan: 2,
                                            centerText: true,
                                        },
                                    ],
                                    [
                                        {
                                            label: 'Events observed',
                                            dataPoint: '128/347 (37%)',
                                            // dataSubPoint: '(38%)'
                                        },
                                        {
                                            label: 'Events observed',
                                            dataPoint: '156/335 (47%)',
                                            // dataSubPoint: '(45%)'
                                        },
                                    ],
                                    [
                                        {
                                            label: 'Median EFS<sup>c</sup>',
                                            dataPoint: '59.7 months',
                                            dataSubPoint: '(95% CI, 37.9–NR)',
                                        },
                                        {
                                            label: 'Median EFS<sup>c</sup>',
                                            dataPoint: '29.6 months',
                                            dataSubPoint: '(95% CI, 19.5–41.9)',
                                        },
                                    ],
                                ]}
                            />
                               

                                 <NotationBlock
                                title=""
                                definitions="NR = not reached; SOC = adjuvant RT ± cisplatin."
                                footnotes={ComparisonTableFootnotes}
                                data={[]}
                            />

                            <FeaturedStat
                               statistic="30%"
                               title="Reduction in risk of an event (progression, recurrence, or death) with KEYTRUDA + SOC vs SOC alone<div style='margin-top: 10px;'><strong>(HR<sup>a</sup>=0.70; 95% CI, 0.55–0.89; <i>P</i><sup>b</sup>=0.00140)</strong></div>"
                            />
                           
                        <TextBlock>                                   
                                  <ul>
                                     <li><strong>EFS subgroup: </strong>In an exploratory subgroup analysis of patients with PD-L1&ndash;positive (CPS ≥1) hypopharyngeal tumors who were randomized (n=51), EFS HR was 2.28 (95% CI, 0.79—6.56)</li>
                                     </ul>
                                 </TextBlock>
                              <TextBlock>
                                  <ul>
                                     <li><strong>Additional efficacy outcome measure:</strong> While OS results were not mature at this interim analysis, with 76% of prespecified OS events in the CPS ≥1 population, no trend towards a detriment was observed</li>
                                     </ul>
                        </TextBlock>
                        </PageSection>

                        <PageSection bgColor="white" title={anchorLinkModelData[1].label}>
                            <NCCNcallout
                                data={NCCNcalloutData}
                                references={NCCNcalloutReferences}
                                definitions={NCCNcalloutDefinitions}
                            />

                        </PageSection>
                
                        <PageSection bgColor="cloud" title={anchorLinkModelData[2].label}>
                            <HNSCC_Squamous />
                        </PageSection>

{/* 
                        <PageSection bgColor="cloud" title={anchorLinkModelData[2].label}>
                            <PageSubSection title="Hypothetical Patients Based on Those Studied in">
                                <PatientProfile {...patientProfileSuzie} />
                            </PageSubSection>
                        </PageSection> */}
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>
                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;